/**
 * Platform (Auth-Kit / WorkOS) session handoff for Hub → new-tab open.
 *
 * Platform opens Knowledge Assistant without `noopener`, then postMessages
 * `{ type, accessToken, user }` after this app signals ready. Tokens are kept
 * in sessionStorage for this tab only.
 *
 * Uses `X-NDC-Access-Token` (not Authorization Bearer) so Azure Easy Auth does
 * not reject a non-Entra token.
 */

export const PLATFORM_AUTH_READY_TYPE = "ndc-ka-ready-v1";
export const PLATFORM_AUTH_MESSAGE_TYPE = "ndc-platform-auth-v1";

const STORAGE_TOKEN_KEY = "ndc_platform_access_token";
const STORAGE_USER_KEY = "ndc_platform_user";

const DEFAULT_PLATFORM_ORIGINS = [
  "https://app-dev.nationaldatacare.com",
  "https://app.nationaldatacare.com",
  "http://localhost:3010",
  "http://127.0.0.1:3010",
];

export type PlatformUser = {
  id: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
};

export type PlatformSession = {
  accessToken: string;
  user: PlatformUser | null;
};

function allowedPlatformOrigins(): Set<string> {
  const fromEnv = (import.meta.env.VITE_PLATFORM_ORIGINS as string | undefined)
    ?.split(",")
    .map((s) => s.trim())
    .filter(Boolean);
  return new Set(fromEnv?.length ? fromEnv : DEFAULT_PLATFORM_ORIGINS);
}

export function isPlatformOrigin(origin: string): boolean {
  if (allowedPlatformOrigins().has(origin)) return true;
  try {
    const host = new URL(origin).hostname;
    return (
      host === "localhost" ||
      host === "127.0.0.1" ||
      host.endsWith(".nationaldatacare.com")
    );
  } catch {
    return false;
  }
}

export function isPlatformLaunch(): boolean {
  if (typeof window === "undefined") return false;
  const params = new URLSearchParams(window.location.search);
  if (params.get("from") === "platform") return true;
  try {
    return Boolean(window.opener && !window.opener.closed);
  } catch {
    return false;
  }
}

export function readPlatformSession(): PlatformSession | null {
  try {
    const accessToken = sessionStorage.getItem(STORAGE_TOKEN_KEY);
    if (!accessToken) return null;
    const rawUser = sessionStorage.getItem(STORAGE_USER_KEY);
    let user: PlatformUser | null = null;
    if (rawUser) {
      const parsed = JSON.parse(rawUser) as PlatformUser;
      if (parsed && typeof parsed.id === "string" && typeof parsed.email === "string") {
        user = parsed;
      }
    }
    return { accessToken, user };
  } catch {
    return null;
  }
}

export function storePlatformSession(session: PlatformSession): void {
  sessionStorage.setItem(STORAGE_TOKEN_KEY, session.accessToken);
  if (session.user) {
    sessionStorage.setItem(STORAGE_USER_KEY, JSON.stringify(session.user));
  } else {
    sessionStorage.removeItem(STORAGE_USER_KEY);
  }
}

export function clearPlatformSession(): void {
  sessionStorage.removeItem(STORAGE_TOKEN_KEY);
  sessionStorage.removeItem(STORAGE_USER_KEY);
}

function normalizeUser(raw: unknown): PlatformUser | null {
  if (!raw || typeof raw !== "object") return null;
  const u = raw as Record<string, unknown>;
  if (typeof u.id !== "string" || typeof u.email !== "string") return null;
  return {
    id: u.id,
    email: u.email,
    firstName: typeof u.firstName === "string" ? u.firstName : null,
    lastName: typeof u.lastName === "string" ? u.lastName : null,
  };
}

/**
 * Wait for Platform opener to post the Auth-Kit access token.
 * Resolves null on timeout / missing opener.
 */
export function awaitPlatformAuthHandoff(timeoutMs = 8000): Promise<PlatformSession | null> {
  const existing = readPlatformSession();
  if (existing) return Promise.resolve(existing);

  if (typeof window === "undefined") return Promise.resolve(null);

  return new Promise((resolve) => {
    let settled = false;
    const finish = (session: PlatformSession | null) => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", onMessage);
      window.clearTimeout(timer);
      resolve(session);
    };

    const onMessage = (event: MessageEvent) => {
      if (!isPlatformOrigin(event.origin)) return;
      const data = event.data as { type?: string; accessToken?: string; user?: unknown };
      if (!data || data.type !== PLATFORM_AUTH_MESSAGE_TYPE) return;
      if (typeof data.accessToken !== "string" || !data.accessToken.trim()) return;
      const session: PlatformSession = {
        accessToken: data.accessToken.trim(),
        user: normalizeUser(data.user),
      };
      storePlatformSession(session);
      finish(session);
    };

    window.addEventListener("message", onMessage);

    try {
      if (window.opener && !window.opener.closed) {
        window.opener.postMessage(
          { type: PLATFORM_AUTH_READY_TYPE },
          // Target origin is restricted on the Platform side when responding.
          "*",
        );
      }
    } catch {
      // Cross-origin opener blocked — handoff will time out.
    }

    const timer = window.setTimeout(() => finish(readPlatformSession()), timeoutMs);
  });
}

/** Headers for Chat-Bot API calls when a Platform WorkOS session is present. */
export function platformAuthHeaders(
  session?: PlatformSession | null,
): Record<string, string> {
  const active = session ?? readPlatformSession();
  if (!active?.accessToken) return {};
  const headers: Record<string, string> = {
    "X-NDC-Access-Token": active.accessToken,
  };
  if (active.user) {
    headers["X-NDC-Platform-User"] = JSON.stringify({
      id: active.user.id,
      email: active.user.email,
      firstName: active.user.firstName ?? null,
      lastName: active.user.lastName ?? null,
    });
  }
  return headers;
}
